/**
 * TITAM AI - Cliente API
 * Auth foi movido para Supabase.
 * Este client continua responsável apenas pelas rotas de dados do backend.
 */

import { supabase } from '../lib/supabaseClient.js'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001/api'

// Fase 4/5: client_id selecionado, injetado automaticamente nas queries de dados.
// O AppContext chama setSelectedClientId(...) quando o usuário escolhe um cliente
// no ClientSwitcher. Mantemos como singleton porque o frontend é SPA
// single-window (sem SSR); se uma rota explicitamente passar `client_id` na
// query, respeitamos o valor.
//
// Semântica do valor:
//   - string<uuid>  → "filtrar apenas este cliente"
//   - null          → "scope agregado": não anexar client_id; backend usa
//                     o conjunto completo de effective_client_ids.
let _selectedClientId = null

export function setSelectedClientId(id) {
  _selectedClientId = id || null
}

function getSelectedClientId() {
  return _selectedClientId
}

// Issue #94: organização ativa da sessão, injetada em toda request via
// header X-Active-Organization-Id (backend em api/access/context.py usa
// isso pra filtrar memberships/clients pra essa organização só — sem isso o
// AccessContext agrega TODAS as organizações do usuário, o que vazava
// clients de uma org pra outra num usuário multi-org). AppContext chama
// setActiveOrganizationId(...) no boot e sempre que o usuário troca de
// organização (gate de login / OrganizationSwitcher).
let _activeOrganizationId = null

export function setActiveOrganizationId(id) {
  _activeOrganizationId = id || null
}

function withClient(params) {
  const qs = params instanceof URLSearchParams ? params : new URLSearchParams(params || {})
  if (_selectedClientId && !qs.has('client_id')) {
    qs.append('client_id', _selectedClientId)
  }
  return qs
}

async function getAccessToken() {
  const { data } = await supabase.auth.getSession()
  const token = data.session?.access_token

  if (!token) return null

  // Decodifica o JWT (sem validar assinatura)
  const payload = JSON.parse(atob(token.split('.')[1]))

  const now = Math.floor(Date.now() / 1000)

  // Se o token ainda "não começou", espera
  if (payload.iat && payload.iat > now) {
    const delay = (payload.iat - now) * 1000 + 100
    await new Promise((res) => setTimeout(res, delay))
  }

  return token
}

export async function fetchApi(path, options = {}) {
  if (import.meta.env.DEV && import.meta.env.MODE === 'demo') {
    return (await import('../demo/data.js')).demoRequest(path, options)
  }
  const url = `${API_URL}${path}`
  const token = await getAccessToken()

  let response
  try {
    response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(_activeOrganizationId ? { 'X-Active-Organization-Id': _activeOrganizationId } : {}),
        ...options.headers,
      },
    })
  } catch (networkError) {
    console.error('[api] erro de rede em', path, networkError)
    throw new Error(`Falha de rede ao chamar ${path}: ${networkError.message}`, { cause: networkError })
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }))
    const detail = err.detail || `API Error: ${response.status}`
    console.error(`[api] ${response.status} em ${path}:`, detail)
    // Importante: NAO derrubamos a sessao Supabase aqui. Um 401 do backend
    // significa apenas que o backend nao validou o token (config errada,
    // SUPABASE_JWT_SECRET ausente, etc). A sessao no client continua valida.
    // detail estruturado ({ message, errors }) vira mensagem legível; o objeto
    // inteiro fica em error.detail pra quem precisa dos campos.
    const error = new Error(typeof detail === 'object' ? (detail.message || JSON.stringify(detail)) : detail)
    error.detail = err.detail
    // Status HTTP anexado pro chamador distinguir falha permanente (404 = link
    // morto) de transiente (502 Evolution fora do ar) — ex.: polling que não
    // deve parar por causa de uma falha passageira.
    error.status = response.status
    throw error
  }

  return response.json()
}

// Fase 4: endpoints canônicos do AccessContext.
export const getMyAccess = ({ refresh = false } = {}) =>
  fetchApi(`/me/access${refresh ? '?refresh=true' : ''}`)
export const getClients = () => fetchApi('/clients')

const ADMIN_TOKEN_KEY = 'deebs:adminToken'

function getAdminToken() {
  try {
    return localStorage.getItem(ADMIN_TOKEN_KEY) || null
  } catch {
    return null
  }
}

function setAdminToken(token) {
  try {
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token)
    else localStorage.removeItem(ADMIN_TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

async function adminFetchApi(path, options = {}) {
  if (import.meta.env.DEV && import.meta.env.MODE === 'demo') {
    return (await import('../demo/data.js')).demoRequest(path, options)
  }
  const url = `${API_URL}${path}`
  const token = getAdminToken()
  let response
  try {
    response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    })
  } catch (networkError) {
    throw new Error(`Falha de rede ao chamar ${path}: ${networkError.message}`, { cause: networkError })
  }

  if (!response.ok) {
    if (response.status === 401) setAdminToken(null)
    const err = await response.json().catch(() => ({ detail: `HTTP ${response.status}` }))
    throw new Error(err.detail || `API Error: ${response.status}`)
  }
  return response.json()
}

// ---------------------------------------------------------------------------
// Painel administrativo standalone. Nao usa sessao Supabase nem usuario comum.
// ---------------------------------------------------------------------------
export const adminLogin = async ({ username, password }) => {
  const result = await adminFetchApi('/admin/auth/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  })
  setAdminToken(result.access_token)
  return result
}
export const adminMe = () => adminFetchApi('/admin/auth/me')
export const adminLogout = () => setAdminToken(null)
export const adminOverview = () => adminFetchApi('/admin/overview')
export const adminListUsers = (q = '') =>
  adminFetchApi(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`)
export const adminGetUser = (userId) => adminFetchApi(`/admin/users/${userId}`)
export const adminSetUserActive = (userId, isActive) =>
  adminFetchApi(`/admin/users/${userId}/active`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: isActive }),
  })
export const adminUpsertMembership = (userId, data) =>
  adminFetchApi(`/admin/users/${userId}/memberships`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
export const adminUpsertClientAccess = (userId, data) =>
  adminFetchApi(`/admin/users/${userId}/client-access`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
export const adminListOrganizations = () => adminFetchApi('/admin/organizations')
export const adminSetOrganizationActive = (organizationId, isActive) =>
  adminFetchApi(`/admin/organizations/${organizationId}/active`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: isActive }),
  })
export const adminListClients = () => adminFetchApi('/admin/clients')
export const adminSetClientActive = (clientId, isActive) =>
  adminFetchApi(`/admin/clients/${clientId}/active`, {
    method: 'PATCH',
    body: JSON.stringify({ is_active: isActive }),
  })
export const adminListInvites = () => adminFetchApi('/admin/invites')
export const adminCreateInvite = ({ organizationId, expiresInDays = null }) =>
  adminFetchApi('/admin/invites', {
    method: 'POST',
    body: JSON.stringify({ organization_id: organizationId, expires_in_days: expiresInDays }),
  })
export const adminRevokeInvite = (inviteId) =>
  adminFetchApi(`/admin/invites/${inviteId}/revoke`, { method: 'POST' })
export const adminListAccessInvites = () => adminFetchApi('/admin/access-invites')
export const adminCreateAccessInvite = () =>
  adminFetchApi('/admin/access-invites', { method: 'POST' })
export const adminDeleteAccessInvite = (inviteId) =>
  adminFetchApi(`/admin/access-invites/${inviteId}`, { method: 'DELETE' })
export const adminListAudit = () => adminFetchApi('/admin/audit')

// Exclusão permanente da própria conta (hard-delete só do usuário; os dados da
// organização são preservados). Backend: DELETE /api/me/account.
export const deleteAccount = () => fetchApi('/me/account', { method: 'DELETE' })

// ---------------------------------------------------------------------------
// Convites de organização (Fluxo 2 — ingresso por convite do owner).
//   - createInvite/listInvites/revokeInvite: gestão pelo owner/admin (Perfil).
//   - previewOrgInvite: público (sem login) — usado na tela de cadastro para
//     validar o código e mostrar a organização-alvo antes de criar a conta.
//   - joinOrganization: pós-cadastro — consome o convite e materializa o
//     acesso (user_client_access) aos clients da org.
// ---------------------------------------------------------------------------
export const createInvite = ({
  expiresInDays = null,
  memberRole = 'viewer',
  clients = null,
} = {}) =>
  fetchApi('/invites', {
    method: 'POST',
    body: JSON.stringify({
      expires_in_days: expiresInDays,
      member_role: memberRole,
      clients,
    }),
  })

export const listInvites = () => fetchApi('/invites')

export const revokeInvite = (inviteId) =>
  fetchApi(`/invites/${inviteId}/revoke`, { method: 'POST' })

// Chaves de API (issue #280): só login de owner/admin; o segredo (`key`) volta
// apenas na criação.
export const listApiKeys = () => fetchApi('/api-keys')

export const createApiKey = (payload) =>
  fetchApi('/api-keys', { method: 'POST', body: JSON.stringify(payload) })

export const revokeApiKey = (keyId) =>
  fetchApi(`/api-keys/${keyId}/revoke`, { method: 'POST' })

export const previewOrgInvite = (code) =>
  fetchApi(`/public/invites/${encodeURIComponent(code)}`)

export const joinOrganization = (code) =>
  fetchApi('/me/join-organization', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })

export const listOrganizationUsers = () => fetchApi('/organization/users')

export const updateOrganizationUserAccess = (userId, data) =>
  fetchApi(`/organization/users/${encodeURIComponent(userId)}/access`, {
    method: 'PUT',
    body: JSON.stringify(data),
  })

export const revokeOrganizationUserAccess = (userId) =>
  fetchApi(`/organization/users/${encodeURIComponent(userId)}/revoke`, {
    method: 'POST',
  })

export const restoreOrganizationUserAccess = (userId) =>
  fetchApi(`/organization/users/${encodeURIComponent(userId)}/restore`, {
    method: 'POST',
  })

export const removeOrganizationUser = (userId) =>
  fetchApi(`/organization/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  })

// Cria um client na org ATIVA do usuário (owner/admin) — o header de
// organização ativa (issue #94, injetado em fetchApi) escopa o backend pra
// essa org, então não precisa (nem deve) ser escolhida aqui de novo. Usado
// no onboarding (is_self=true) e no fluxo manual de vinculação (criar client
// inline). `domain` é a URL do site do cliente — obrigatória para clients
// comuns.
export const createClient = ({ name, isSelf = false, domain = null }) =>
  fetchApi('/clients', {
    method: 'POST',
    body: JSON.stringify({ name, is_self: isSelf, domain }),
  })

// Edita nome, URL (domain) e/ou domínios alternativos do tracker de um client.
// Backend sincroniza ad_accounts.domain ao mudar `domain`.
export const updateClient = (clientId, { name, domain, alternate_domains } = {}) =>
  fetchApi(`/clients/${clientId}`, {
    method: 'PATCH',
    body: JSON.stringify({
      ...(name !== undefined ? { name } : {}),
      ...(domain !== undefined ? { domain } : {}),
      ...(alternate_domains !== undefined ? { alternate_domains } : {}),
    }),
  })

export const getDashboard = (startDate, endDate, campaignIds = []) => {
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  ;(campaignIds || []).forEach((id) => params.append('campaignIds', id))
  return fetchApi(`/dashboard/summary?${withClient(params)}`)
}

export const getDashboardWidgets = (startDate, endDate, campaignIds = [], metric = 'leads') => {
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  if (metric) params.append('metric', metric)
  ;(campaignIds || []).forEach((id) => params.append('campaignIds', id))
  return fetchApi(`/dashboard/widgets?${withClient(params)}`)
}

// ---------------------------------------------------------------------------
// Módulo Mapa — origem geográfica por fonte, com filtros (/api/mapa/*).
// `filters` = { origem: [..], midia: [..], campanha: [..], pagina: [..],
// referer: [..], dispositivo: [..], palavra: [..] }; cada valor vira um
// parâmetro repetido.
// ---------------------------------------------------------------------------
function mapaParams(base, filters) {
  const params = new URLSearchParams()
  Object.entries(base).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.append(k, v)
  })
  Object.entries(filters || {}).forEach(([dim, values]) => {
    ;(values || []).forEach((v) => params.append(dim, v))
  })
  return params
}

export const getMapaGeo = ({ startDate, endDate, source, metric, filters, clientId }) =>
  fetchApi(`/mapa/geo?${mapaParams({ startDate, endDate, source, metric, client_id: clientId }, filters)}`)

export const getMapaCity = ({ startDate, endDate, source, metric, uf, cityKeys, filters, clientId }) => {
  const params = mapaParams({ startDate, endDate, source, metric, uf, client_id: clientId }, filters)
  ;(cityKeys || []).forEach((k) => params.append('cityKey', k))
  return fetchApi(`/mapa/city?${params}`)
}

export const getMapaOptions = ({ dimension, startDate, endDate, source, q, filters, clientId }) =>
  fetchApi(`/mapa/options?${mapaParams({ dimension, startDate, endDate, source, q, client_id: clientId }, filters)}`)

// ---------------------------------------------------------------------------
// Módulo SEO — tráfego orgânico (user_data sem gclid).
// Estrutura espelha getDashboard / getDashboardWidgets para reutilização
// dos componentes visuais. Quando o Google Search Console for integrado,
// esses endpoints passarão a unir dados de user_data + GSC.
// ---------------------------------------------------------------------------
export const getSEOSummary = (startDate, endDate) => {
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  return fetchApi(`/seo/summary?${withClient(params)}`)
}

export const getSEOWidgets = (startDate, endDate, metric = 'leads') => {
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  if (metric) params.append('metric', metric)
  return fetchApi(`/seo/widgets?${withClient(params)}`)
}

// ---------------------------------------------------------------------------
// DRE — visão financeira/comercial mensal por ano calendário (mín. 2026).
// Embutido na dashboard principal. Payload genérico em blocos (hoje só Google
// Ads). client_id injetado pelo withClient — mesmo scope multi-tenant.
// ---------------------------------------------------------------------------
export const getDRE = (year, granularity) => {
  const params = new URLSearchParams()
  if (year) params.append('year', String(year))
  if (granularity) params.append('granularity', granularity)
  return fetchApi(`/dre/summary?${withClient(params)}`)
}

export const getCampaigns = () => {
  const params = withClient(new URLSearchParams())
  const qs = params.toString()
  return fetchApi(qs ? `/campaigns?${qs}` : '/campaigns')
}

export const getSalesMetrics = (startDate, endDate) => {
  const params = new URLSearchParams()
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)
  return fetchApi(`/sales-metrics?${withClient(params)}`)
}

// Total canônico de cliques no WhatsApp (mesma definição do DRE/Dashboard).
// Usado pelo card agregado da página Google Ads para bater com a Dashboard,
// já que a soma por conta de /sales-metrics descarta cliques com gclid válido
// mas sem ad_account_id atribuído.
export const getWhatsappTotal = (startDate, endDate) => {
  const params = new URLSearchParams()
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)
  return fetchApi(`/sales-metrics/whatsapp-total?${withClient(params)}`)
}

export const getAccounts = () => {
  const params = withClient(new URLSearchParams())
  const qs = params.toString()
  return fetchApi(qs ? `/accounts?${qs}` : '/accounts')
}

export const getGoogleAdsMetrics = (level = 'campaign', startDate, endDate) => {
  const params = new URLSearchParams()
  params.append('level', level)

  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)

  return fetchApi(`/google-ads/metrics?${withClient(params)}`)
}

function normalizeLeadParams(params = {}) {
  const normalized = { ...params }
  if (normalized.startDate && !normalized.start_date) {
    normalized.start_date = normalized.startDate
    delete normalized.startDate
  }
  if (normalized.endDate && !normalized.end_date) {
    normalized.end_date = normalized.endDate
    delete normalized.endDate
  }
  return normalized
}

export const getLeads = (params = {}) => {
  const search = withClient(new URLSearchParams(normalizeLeadParams(params)))
  return fetchApi(`/leads?${search}`)
}

export const getLeadStats = (params = {}) => {
  const search = withClient(new URLSearchParams(normalizeLeadParams(params)))
  return fetchApi(`/leads/stats?${search}`)
}

export const updateLead = (id, data) => {
  const search = withClient(new URLSearchParams())
  const qs = search.toString() ? `?${search}` : ''
  return fetchApi(`/leads/${id}${qs}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export const getLeadActivities = (id) => {
  const search = withClient(new URLSearchParams())
  const qs = search.toString() ? `?${search}` : ''
  return fetchApi(`/leads/${id}/activities${qs}`)
}

export const getConnections = () => {
  // OAuth connections são por organização, não por client_id — não
  // injetamos client_id aqui.
  return fetchApi('/connections')
}

// Status da coleta (fila sync_jobs). Retorna { jobs: [...], active: bool }.
// `active` = há job pending/running → o banner do dashboard continua o polling.
// Jobs são por organização (não por client_id) — sem withClient.
export const getSyncJobs = () => fetchApi('/sync-jobs')

// ---------------------------------------------------------------------------
// Google Ads OAuth — funções consumidas por:
//   - pages/auth/Onboarding.jsx (step 2 opcional)
//   - pages/integrations/GoogleAdsCallback.jsx
//   - pages/integrations/GoogleAdsAccountPicker.jsx
//   - pages/Connections.jsx
// ---------------------------------------------------------------------------
export const startGoogleAdsOAuth = () =>
  fetchApi('/integrations/google-ads/authorize', { method: 'POST' })

export const completeGoogleAdsOAuth = ({ code, state }) =>
  fetchApi('/integrations/google-ads/callback', {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  })

export const linkGoogleAdsAccounts = ({ connectionId, accounts }) =>
  fetchApi('/integrations/google-ads/link-accounts', {
    method: 'POST',
    body: JSON.stringify({
      connection_id: connectionId,
      accounts,
    }),
  })

// Gerenciar uma conexão que já existe (#355): contas do login (sem novo consent) e exclusão.
export const listGoogleAdsConnectionAccounts = (connectionId) =>
  fetchApi(`/integrations/google-ads/connections/${connectionId}/accounts`)

export const deleteGoogleAdsConnection = (connectionId) =>
  fetchApi(`/integrations/google-ads/connections/${connectionId}`, { method: 'DELETE' })

export const disconnectGoogleAds = (connectionId) =>
  fetchApi('/integrations/google-ads/disconnect', {
    method: 'POST',
    body: JSON.stringify({ connection_id: connectionId }),
  })

// ---------------------------------------------------------------------------
// Meta Ads OAuth — módulo NOVO, isolado do Google. Consumido por:
//   - pages/integrations/MetaAdsCallback.jsx
//   - pages/integrations/MetaAdsAccountPicker.jsx
//   - pages/MetaAds.jsx (connect + status)
// Conexões/jobs Meta são por organização — sem withClient.
// ---------------------------------------------------------------------------
// `management: true` pede também ads_management (reautorização opcional da gestão de anúncios).
// `product` (#320): 'ads' (padrão), 'facebook' ou 'instagram'. Cada produto tem a sua autorização e conexão.
export const startMetaAdsOAuth = ({ management = false, product = 'ads' } = {}) =>
  fetchApi(`/meta-ads/authorize?${new URLSearchParams({ product, ...(management ? { management: 'true' } : {}) })}`, { method: 'POST' })

// Gestão de anúncios: sem `confirm` o backend só devolve a prévia (nada é enviado à Meta).
export const manageMetaStatus = ({ clientId, level, entityId, status, confirm = false }) =>
  fetchApi('/meta-ads/manage/status', {
    method: 'POST',
    body: JSON.stringify({ client_id: clientId, level, entity_id: String(entityId), status, confirm }),
  })

export const manageMetaBudget = ({ clientId, level, entityId, kind = 'daily_budget', amount, confirm = false }) =>
  fetchApi('/meta-ads/manage/budget', {
    method: 'POST',
    body: JSON.stringify({ client_id: clientId, level, entity_id: String(entityId), kind, amount, confirm }),
  })

// Nova campanha (campanha + conjunto, sempre pausados). `body` vem de `buildRequest` em
// lib/metaCampaignForm.js; com `confirm: false` o backend só valida e devolve a prévia.
export const createMetaCampaign = (body) =>
  fetchApi('/meta-ads/manage/campaign', { method: 'POST', body: JSON.stringify(body) })

// Modelo de UTM dos anúncios da Meta (#318): um por cliente, com as macros da Meta.
export const getMetaUtmTemplate = (clientId) =>
  fetchApi(`/meta-ads/utm-template?${new URLSearchParams({ client_id: clientId })}`)

export const saveMetaUtmTemplate = ({ clientId, utm }) =>
  fetchApi('/meta-ads/utm-template', { method: 'PUT', body: JSON.stringify({ client_id: clientId, ...utm }) })

// Conversions API (#317): configuração por cliente, mapa etapa -> evento, teste e log de envios.
export const getMetaConversionsSettings = (clientId) =>
  fetchApi(`/meta-ads/conversions/settings?${new URLSearchParams({ client_id: clientId })}`)

export const getMetaConversionsDatasets = ({ clientId, accountId }) =>
  fetchApi(`/meta-ads/conversions/datasets?${new URLSearchParams({ client_id: clientId, meta_account_id: String(accountId) })}`)

export const saveMetaConversionsSettings = (body) =>
  fetchApi('/meta-ads/conversions/settings', { method: 'PUT', body: JSON.stringify(body) })

export const sendMetaConversionsTest = ({ clientId, code }) =>
  fetchApi('/meta-ads/conversions/test', { method: 'POST', body: JSON.stringify({ client_id: clientId, test_event_code: code }) })

export const getMetaConversionsLog = ({ clientId, result = '', limit = 50 }) =>
  fetchApi(`/meta-ads/conversions/log?${new URLSearchParams({ client_id: clientId, limit: String(limit), ...(result ? { result } : {}) })}`)

export const retryMetaConversion = ({ clientId, id }) =>
  fetchApi(`/meta-ads/conversions/log/${encodeURIComponent(id)}/retry?${new URLSearchParams({ client_id: clientId })}`, { method: 'POST' })

// Regras de objetivo/meta/destino, Páginas, pixels e públicos da conta.
export const getMetaCampaignOptions = ({ clientId, accountId }) =>
  fetchApi(`/meta-ads/manage/campaign/options?${new URLSearchParams({ client_id: clientId, meta_account_id: String(accountId) })}`)

// Envia a imagem (`image`) ou o vídeo (`video`) do anúncio à biblioteca da conta, em corpo binário.
// Não cria nem altera anúncio; devolve { id } (hash da imagem ou id do vídeo).
export const uploadMetaCampaignMedia = ({ clientId, accountId, kind, file }) =>
  fetchApi(
    `/meta-ads/manage/campaign/media?${new URLSearchParams({ client_id: clientId, meta_account_id: String(accountId), kind, filename: file.name })}`,
    { method: 'POST', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } },
  )

// Formulários de lead de uma Página (anúncio de leads). Falha da Meta volta como { forms: [], warning }.
export const getMetaLeadForms = ({ clientId, accountId, pageId }) =>
  fetchApi(`/meta-ads/manage/campaign/lead-forms?${new URLSearchParams({ client_id: clientId, meta_account_id: String(accountId), meta_page_id: String(pageId) })}`)

// Busca de locais (`geo`) e interesses (`interest`) na Meta.
export const searchMetaTargeting = ({ clientId, accountId, kind, q }) =>
  fetchApi(`/meta-ads/manage/campaign/search?${new URLSearchParams({ client_id: clientId, meta_account_id: String(accountId), kind, q })}`)

export const duplicateMetaCampaign = ({ clientId, entityId, confirm = false }) =>
  fetchApi('/meta-ads/manage/campaign/duplicate', {
    method: 'POST',
    body: JSON.stringify({ client_id: clientId, level: 'campaign', entity_id: String(entityId), confirm }),
  })

export const getMetaManageAudit = () =>
  fetchApi(`/meta-ads/manage/audit?${withClient(new URLSearchParams())}`)

export const completeMetaAdsOAuth = ({ code, state }) =>
  fetchApi('/meta-ads/callback', {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  })

export const linkMetaAdsAccounts = ({ connectionId, accounts }) =>
  fetchApi('/meta-ads/link-accounts', {
    method: 'POST',
    body: JSON.stringify({
      connection_id: connectionId,
      accounts,
    }),
  })

export const listMetaAdsConnectionAccounts = (connectionId) =>
  fetchApi(`/meta-ads/connections/${connectionId}/accounts`)

export const deleteMetaConnection = (connectionId) =>
  fetchApi(`/meta-ads/connections/${connectionId}`, { method: 'DELETE' })

export const disconnectMetaAds = (connectionId) =>
  fetchApi('/meta-ads/disconnect', {
    method: 'POST',
    body: JSON.stringify({ connection_id: connectionId }),
  })

export const getMetaConnections = () => fetchApi('/meta-ads/connections')

// Lead Ads — Páginas do Facebook. As Páginas disponíveis vêm da Meta (sem token);
// o vínculo é por cliente e assina o evento `leadgen` no webhook.
export const getMetaPagesAvailable = (connectionId) =>
  fetchApi(`/meta-ads/pages/available?connection_id=${encodeURIComponent(connectionId)}`)

export const getMetaPages = () => fetchApi('/meta-ads/pages')

// Instagram Direct e Messenger no Atendimento (#344). Os canais disponíveis vêm da conexão
// `messaging` na Meta (sem token); o vínculo é por cliente e assina `messages` na Página.
export const getMetaMessagingAvailable = (connectionId) =>
  fetchApi(`/meta-ads/messaging/available?connection_id=${encodeURIComponent(connectionId)}`)

export const getMetaMessagingChannels = (clientId) =>
  fetchApi(`/meta-ads/messaging/channels?client_id=${encodeURIComponent(clientId)}`)

export const linkMetaMessaging = ({ connectionId, clientId, channels }) =>
  fetchApi('/meta-ads/messaging/link', {
    method: 'POST',
    body: JSON.stringify({ connection_id: connectionId, client_id: clientId, channels }),
  })

export const unlinkMetaMessaging = (channelId) =>
  fetchApi(`/meta-ads/messaging/channels/${encodeURIComponent(channelId)}`, { method: 'DELETE' })

// Troca o token da conexão (token de usuário de sistema gerado no Business Manager).
// O backend valida app, validade e escopos na Meta antes de gravar.
export const replaceMetaConnectionToken = (connectionId, accessToken) =>
  fetchApi(`/meta-ads/connections/${encodeURIComponent(connectionId)}/token`, {
    method: 'POST',
    body: JSON.stringify({ access_token: accessToken }),
  })

export const linkMetaPages = ({ connectionId, clientId, pageIds }) =>
  fetchApi('/meta-ads/pages/link', {
    method: 'POST',
    body: JSON.stringify({ connection_id: connectionId, client_id: clientId, page_ids: pageIds }),
  })

// Métricas Meta (dashboard). client_id injetado por withClient (tenant).
// `accountIds` (opcional): restringe a contas Meta do cliente; vazio = todas somadas.
const appendMetaAccounts = (params, accountIds) => {
  for (const id of accountIds || []) params.append('meta_account_id', id)
  return params
}

export const getMetaAdsSummary = (startDate, endDate, accountIds) => {
  const params = new URLSearchParams()
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)
  appendMetaAccounts(params, accountIds)
  return fetchApi(`/meta-ads/summary?${withClient(params)}`)
}

export const getMetaAdsAccounts = () => {
  const search = withClient(new URLSearchParams())
  const qs = search.toString() ? `?${search}` : ''
  return fetchApi(`/meta-ads/accounts${qs}`)
}

export const getMetaAdsMetrics = (level = 'campaign', startDate, endDate, accountIds) => {
  const params = new URLSearchParams()
  params.append('level', level)
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)
  appendMetaAccounts(params, accountIds)
  return fetchApi(`/meta-ads/metrics?${withClient(params)}`)
}

// Orgânico (Instagram e Página). client_id injetado por withClient (tenant).
const organicQuery = (platform, startDate, endDate, extra = {}) => {
  const params = new URLSearchParams(extra)
  if (platform) params.append('platform', platform)
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)
  return withClient(params)
}

export const getMetaOrganicAccounts = (platform) =>
  fetchApi(`/meta-ads/organic/accounts?${organicQuery(platform)}`)

export const getMetaOrganicProfile = (pageId) =>
  fetchApi(`/meta-ads/organic/profile?${withClient(new URLSearchParams({ page_id: pageId }))}`)

export const getMetaOrganicHistory = (pageId, startDate, endDate) =>
  fetchApi(`/meta-ads/organic/history?${organicQuery('instagram', startDate, endDate, { page_id: pageId })}`)

export const getMetaOrganicSummary = (platform, startDate, endDate, pageId) =>
  fetchApi(`/meta-ads/organic/summary?${organicQuery(platform, startDate, endDate, pageId ? { page_id: pageId } : {})}`)

export const getMetaOrganicPosts = (platform, startDate, endDate, pageId) =>
  fetchApi(`/meta-ads/organic/posts?${organicQuery(platform, startDate, endDate, { limit: '50', ...(pageId ? { page_id: pageId } : {}) })}`)

export const getMetaOrganicFollowers = (platform, startDate, endDate, pageId) =>
  fetchApi(`/meta-ads/organic/followers?${organicQuery(platform, startDate, endDate, pageId ? { page_id: pageId } : {})}`)

export const getMetaOrganicComments = (platform, startDate, endDate, pageId) =>
  fetchApi(`/meta-ads/organic/comments?${organicQuery(platform, startDate, endDate, { limit: '100', ...(pageId ? { page_id: pageId } : {}) })}`)

export const syncMetaOrganic = () =>
  fetchApi(`/meta-ads/organic/sync?${withClient(new URLSearchParams())}`, { method: 'POST' })

export const getMetaAdsBreakdowns = (kind, startDate, endDate, accountIds) => {
  const params = new URLSearchParams()
  params.append('kind', kind)
  if (startDate) params.append('start_date', startDate)
  if (endDate) params.append('end_date', endDate)
  appendMetaAccounts(params, accountIds)
  return fetchApi(`/meta-ads/breakdowns?${withClient(params)}`)
}
export const getDevices = (startDate, endDate) => {
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  return fetchApi(`/analytics/devices?${withClient(params)}`)
}
export const getGeo = (startDate, endDate) => {
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  return fetchApi(`/analytics/geo?${withClient(params)}`)
}
export const getKeywords = (startDate, endDate, accountIds = []) => {
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  // FastAPI parseia query repetida como list[str] — `account_id=a&account_id=b`.
  for (const id of accountIds || []) {
    if (id) params.append('account_id', String(id))
  }
  return fetchApi(`/analytics/keywords?${withClient(params)}`)
}
export const getLandingPages = (startDate, endDate) => {
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  return fetchApi(`/analytics/landing-pages?${withClient(params)}`)
}
export const syncToday = () =>
  fetchApi('/sync-today', {
    method: 'POST',
  })
export const getPeriodProjection = (params = {}) => {
  if (!params.base_start || !params.proj_start || !params.proj_end) {
    throw new Error('Parâmetros obrigatórios: base_start, proj_start, proj_end')
  }

  const search = withClient(new URLSearchParams(params))
  return fetchApi(`/projections/period-simulate?${search}`)
}
export const getTimeline = (startDate, endDate) => {
  const params = new URLSearchParams()
  if (startDate) params.append('startDate', startDate)
  if (endDate) params.append('endDate', endDate)
  return fetchApi(`/analytics/timeline?${withClient(params)}`)
}

// Auth legado removido do client principal.
const login = () => {
  throw new Error('Login agora é feito via Supabase Auth.')
}

// --- WhatsApp / Evolution ---------------------------------------------------
// refresh=true consulta o estado ao vivo na Evolution e sincroniza o banco.
export const listWhatsappInstances = ({ refresh = false } = {}) =>
  fetchApi(`/whatsapp/instances${refresh ? '?refresh=true' : ''}`)

// Nome real da instância do client: a que já existe no banco (pode não seguir
// o padrão `deebs-<client_id>`, ex.: após unificar clients) ou o padrão.
export const resolveWhatsappInstanceName = async (clientId) => {
  try {
    const instances = await listWhatsappInstances()
    const found = (instances || []).find((i) => i.client_id === clientId)
    if (found?.instance_name) return found.instance_name
  } catch {
    /* cai no padrão */
  }
  return `deebs-${clientId}`
}

export const createWhatsappInstance = ({ instanceName, clientId }) =>
  fetchApi('/whatsapp/instances', {
    method: 'POST',
    body: JSON.stringify({ instance_name: instanceName, client_id: clientId }),
  })

// Estado + QR atual da instância. O front faz polling a cada 40s.
export const getWhatsappInstanceState = (instanceName) =>
  fetchApi(`/whatsapp/instances/${encodeURIComponent(instanceName)}/state`)

// Gera o link público (token) para o cliente escanear o QR sem login.
export const createWhatsappConnectLink = (instanceName) =>
  fetchApi(`/whatsapp/instances/${encodeURIComponent(instanceName)}/connect-link`, {
    method: 'POST',
  })

// Desloga (sem apagar) a instância na Evolution p/ forçar sessão limpa (QR
// que não pareia mais). Preserva o histórico de conversas guardado na
// Evolution — só a sessão do WhatsApp é encerrada.
export const resetWhatsappInstance = (instanceName) =>
  fetchApi(`/whatsapp/instances/${encodeURIComponent(instanceName)}/reset`, {
    method: 'POST',
  })

// Endpoint PÚBLICO (sem JWT) consumido pela página /conectar-whatsapp/:token.
// Devolve apenas { state, qrcode }.
export const getPublicWhatsappConnect = (token) =>
  fetchApi(`/public/whatsapp/connect/${encodeURIComponent(token)}`)

// Pairing code (8 dígitos) alternativo ao QR, pelo mesmo token público.
export const getPublicWhatsappPairingCode = (token, number) =>
  fetchApi(`/public/whatsapp/connect/${encodeURIComponent(token)}/pairing-code`, {
    method: 'POST',
    body: JSON.stringify({ number }),
  })

// ---------------------------------------------------------------------------
// WhatsApp Oficial (Meta Cloud API + CoEx) — módulo paralelo à Evolution.
//   onboardWhatsappMeta: conclui o Embedded Signup CoEx. O front já obteve
//     waba_id + phone_number_id + code via FB.login (coexistence); o backend
//     troca o code por um business token do cliente e grava a conta.
//   listWhatsappMetaAccounts: contas Meta das orgs do usuário (sem token).
// ---------------------------------------------------------------------------
export const onboardWhatsappMeta = ({ clientId, code, wabaId, phoneNumberId }) =>
  fetchApi('/whatsapp/meta/onboard', {
    method: 'POST',
    body: JSON.stringify({
      client_id: clientId,
      code,
      waba_id: wabaId,
      phone_number_id: phoneNumberId,
    }),
  })

export const listWhatsappMetaAccounts = () => fetchApi('/whatsapp/meta/accounts')

// Desconecta (revoga) uma conta Meta. Não apaga histórico — marca revoked.
export const disconnectWhatsappMeta = (accountId) =>
  fetchApi(`/whatsapp/meta/accounts/${encodeURIComponent(accountId)}`, {
    method: 'DELETE',
  })

// Templates (HSM) da WABA. Fora da janela de 24h só template APPROVED envia (#28).
export const listWhatsappMetaTemplates = (accountId) =>
  fetchApi(`/whatsapp/meta/accounts/${encodeURIComponent(accountId)}/templates`)

// Submete template novo à aprovação da Meta (fica PENDING até a revisão).
export const createWhatsappMetaTemplate = (accountId, { name, language = 'pt_BR', category, components }) =>
  fetchApi(`/whatsapp/meta/accounts/${encodeURIComponent(accountId)}/templates`, {
    method: 'POST',
    body: JSON.stringify({ name, language, category, components }),
  })

// ---------------------------------------------------------------------------
// CRM / Comercial — funil dinâmico (Fase 1 / migration 015, DEAL-CENTRIC).
// Módulo NOVO, separado do Google Ads (NÃO confundir com /leads). Endpoints
// sob /api/crm. client_id é injetado por withClient (query) nas leituras e
// mutações por id; nas criações (pipeline/contact/deal) vai no corpo via
// getSelectedClientId(), pois o backend lê client_id do body nesses casos.
//
// Modelagem: o CARD do kanban é o DEAL (única entidade com posição de funil; só
// ela se move → deal_stage_transitions). Os Leads (contacts) pertencem ao Deal
// via deal_contacts (M:N) e NÃO têm pipeline_id/stage_id.
// ---------------------------------------------------------------------------
export const getComercialDashboard = ({
  from, to, pipeline, etapa, vendedor, origem, produto, risk_sort, risk_direction,
} = {}) => {
  const params = withClient(new URLSearchParams())
  if (from) params.set('from', from)
  if (to) params.set('to', to)
  if (pipeline) params.set('pipeline', pipeline)
  if (etapa) params.set('etapa', etapa)
  if (vendedor) params.set('vendedor', vendedor)
  if (origem) params.set('origem', origem)
  if (produto) params.set('produto', produto)
  if (risk_sort) params.set('risk_sort', risk_sort)
  if (risk_direction) params.set('risk_direction', risk_direction)
  return fetchApi(`/crm/dashboard?${params}`)
}

export const getComercialUsers = () => {
  const params = withClient(new URLSearchParams())
  return fetchApi(`/crm/dashboard/users?${params}`)
}

export const getComercialCreated = ({ from, to, pipeline, etapa, vendedor, origem, produto, offset = 0, sort, direction } = {}) => {
  const params = withClient(new URLSearchParams({ offset: String(offset) }))
  Object.entries({ from, to, pipeline, etapa, vendedor, origem, produto, sort, direction }).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  return fetchApi(`/crm/dashboard/created?${params}`)
}

export const getComercialClosed = ({ from, to, pipeline, etapa, vendedor, origem, produto, offset = 0, sort, direction } = {}) => {
  const params = withClient(new URLSearchParams({ offset: String(offset) }))
  Object.entries({ from, to, pipeline, etapa, vendedor, origem, produto, sort, direction }).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  return fetchApi(`/crm/dashboard/closed?${params}`)
}

export const getComercialWon = ({ from, to, pipeline, etapa, vendedor, origem, produto, offset = 0, sort, direction } = {}) => {
  const params = withClient(new URLSearchParams({ offset: String(offset) }))
  Object.entries({ from, to, pipeline, etapa, vendedor, origem, produto, sort, direction }).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  return fetchApi(`/crm/dashboard/won?${params}`)
}

export const getComercialLost = ({ from, to, pipeline, etapa, vendedor, origem, produto, offset = 0, sort, direction } = {}) => {
  const params = withClient(new URLSearchParams({ offset: String(offset) }))
  Object.entries({ from, to, pipeline, etapa, vendedor, origem, produto, sort, direction }).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  return fetchApi(`/crm/dashboard/lost?${params}`)
}

export const getComercialRisks = ({ to, pipeline, etapa, vendedor, origem, produto, offset = 0, sort, direction } = {}) => {
  const params = withClient(new URLSearchParams({ offset: String(offset) }))
  Object.entries({ to, pipeline, etapa, vendedor, origem, produto, sort, direction }).forEach(([key, value]) => {
    if (value) params.set(key, value)
  })
  return fetchApi(`/crm/dashboard/risks?${params}`)
}

export const getPipelines = (tipo) => {
  const params = withClient(new URLSearchParams(tipo ? { tipo } : {}))
  return fetchApi(`/crm/pipelines?${params}`)
}

// Templates de funil (fonte central no backend). Renderizados no modal de criação.
export const getPipelineTemplates = () => fetchApi('/crm/pipeline-templates')

// Cria um funil. `template` (chave de /pipeline-templates) semeia as etapas
// padrão e define o tipo; sem template (ou 'vazio') nasce sem etapas.
export const createPipeline = ({ nome, tipo = 'comercial', descricao = null, template = null }) =>
  fetchApi('/crm/pipelines', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), nome, tipo, descricao, template }),
  })

// Cria o funil padrão + etapas (idempotente). Usar quando o client não tem funil.
export const seedDefaultPipeline = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/pipelines/seed-default?${qs}`, { method: 'POST' })
}

// Usuários da org (p/ responsável do deal).
export const getCrmMembers = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/members?${qs}`)
}

export const updatePipeline = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/pipelines/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const deletePipeline = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/pipelines/${id}?${qs}`, { method: 'DELETE' })
}

export const getStages = (pipelineId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/pipelines/${pipelineId}/stages?${qs}`)
}

export const createStage = (pipelineId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/pipelines/${pipelineId}/stages?${qs}`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export const updateStage = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/stages/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const deleteStage = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/stages/${id}?${qs}`, { method: 'DELETE' })
}

// Companies — crm.companies, tabela própria (não relacionada à migração
// contacts/user_data). `search` filtra por nome, razão social, nome
// fantasia e começo do CNPJ. `limit` padrão 20 (máx. 500).
export const getCompanies = (search, limit) => {
  const params = withClient(new URLSearchParams({
    ...(search ? { search } : {}),
    ...(limit ? { limit: String(limit) } : {}),
  }))
  return fetchApi(`/crm/companies?${params}`)
}

// Empresa + CNAEs, sócios, canais de contato e negócios vinculados.
export const getCompanyDetails = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/companies/${id}/detalhes?${qs}`)
}

export const createCompany = (data) =>
  fetchApi('/crm/companies', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })

export const getCompany = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/companies/${id}?${qs}`)
}

export const updateCompany = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/companies/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

// Empresas — base pública de CNPJ (issues #115/#122). Filtros: nome_fantasia,
// cnpj, uf, cidade, cnae, cnae_secundarios, situacao_cadastral, porte (lista),
// opcao_simples, data_abertura_de, data_abertura_ate, page, page_size.
// Lista vira parâmetro repetido (porte=mei&porte=me); false/vazio não vai.
// 503 = base pública fora do ar — a tela mostra aviso.
export const searchEmpresasPublicas = (filters) => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters || {})) {
    if (Array.isArray(value)) value.forEach((v) => params.append(key, v))
    else if (value !== '' && value != null && value !== false) params.append(key, String(value))
  }
  return fetchApi(`/empresas/publicas?${withClient(params)}`)
}

// Matriz + filiais do mesmo CNPJ básico, ao vivo na base pública (até 200).
export const getEstabelecimentosPublicos = (cnpj) => {
  const params = withClient(new URLSearchParams())
  return fetchApi(`/empresas/publicas/${encodeURIComponent(cnpj)}/estabelecimentos?${params}`)
}

export const importarEmpresas = (cnpjs) =>
  fetchApi('/empresas/importar', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), cnpjs }),
  })

// Contacts — a pessoa/lead (sem posição de funil; não é o card). `search` filtra
// por nome/email/telefone.
export const getCrmContacts = (search, formId) => {
  const base = {}
  if (search) base.search = search
  if (formId) base.form_id = formId
  const params = withClient(new URLSearchParams(base))
  return fetchApi(`/crm/contacts?${params}`)
}

// Campos personalizados do lead (#306): definições por cliente e valores por lead.
export const getLeadCustomFields = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/lead-custom-fields?${qs}`)
}

export const createLeadCustomField = (data) =>
  fetchApi('/crm/lead-custom-fields', {
    method: 'POST',
    body: JSON.stringify({ ...data, client_id: getSelectedClientId() }),
  })

export const updateLeadCustomField = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/lead-custom-fields/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const deleteLeadCustomField = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/lead-custom-fields/${id}?${qs}`, { method: 'DELETE' })
}

// `values` = { chave: valor }; valor vazio remove o campo do lead.
export const setContactCustomFields = (contactId, values) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contacts/${contactId}/custom-fields?${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ values }),
  })
}

export const getCrmContact = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contacts/${id}?${qs}`)
}

export const createCrmContact = (data) =>
  fetchApi('/crm/contacts', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })

export const updateCrmContact = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contacts/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

// Telefones/e-mails do lead (vários por lead). `values` é a lista completa e
// ordenada do tipo ('phone' | 'email'); o primeiro vira o principal.
export const getContactChannels = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contacts/${id}/channels?${qs}`)
}

export const saveContactChannels = (id, channelType, values) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contacts/${id}/channels?${qs}`, {
    method: 'PUT',
    body: JSON.stringify({ channel_type: channelType, values }),
  })
}

export const deleteCrmContact = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contacts/${id}?${qs}`, { method: 'DELETE' })
}

// Clientes — DERIVADO (contact com >=1 deal ganho). Sem entidade própria: a
// lista/KPIs saem de contacts + deals ganhos no backend. O perfil do cliente
// reusa as funções de contact (getCrmContact/getCrmContactDeals/…). `search`
// filtra por nome/email/telefone/empresa.
export const getClientes = (search) => {
  const params = withClient(new URLSearchParams(search ? { search } : {}))
  return fetchApi(`/crm/clientes?${params}`)
}

// KPIs do dashboard de Clientes (total/ativos/inativos/receita/ticket/novos).
export const getClientesSummary = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/clientes/summary?${qs}`)
}

// Deals — o CARD do kanban. `pipelineId` filtra os cards de um funil.
// `limitPerStage` devolve só os N mais recentes de cada etapa; com `stageId` +
// `offset` busca a próxima página de uma coluna ("carregar mais").
export const getDeals = (pipelineId, { archived = 'exclude', limitPerStage, stageId, offset } = {}) => {
  const params = withClient(new URLSearchParams(pipelineId ? { pipeline_id: pipelineId } : {}))
  if (archived && archived !== 'exclude') params.set('archived', archived)
  if (limitPerStage) params.set('limit_per_stage', String(limitPerStage))
  if (stageId) params.set('stage_id', stageId)
  if (offset) params.set('offset', String(offset))
  return fetchApi(`/crm/deals?${params}`)
}

// Todos os negócios do funil em versão enxuta (totais por coluna e estatísticas).
export const getDealsSummary = (pipelineId, { archived = 'exclude' } = {}) => {
  const params = withClient(new URLSearchParams(pipelineId ? { pipeline_id: pipelineId } : {}))
  if (archived && archived !== 'exclude') params.set('archived', archived)
  return fetchApi(`/crm/deals/summary?${params}`)
}

// Só os contadores de WhatsApp (whatsapp_unanswered_count, has_whatsapp) dos
// negócios pedidos: polling leve do kanban, sem recarregar os cards (#390).
export const getDealsWhatsappCounters = (dealIds) =>
  fetchApi('/crm/deals/whatsapp-counters', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), deal_ids: dealIds }),
  })

export const getDeal = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${id}?${qs}`)
}

export const createDeal = (data) =>
  fetchApi('/crm/deals', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })

export const updateDeal = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const deleteDeal = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${id}?${qs}`, { method: 'DELETE' })
}

// Histórico de movimento do deal (deal_stage_transitions).
export const getDealTransitions = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${id}/transitions?${qs}`)
}

// Deal 360 — atividades e timeline do deal (espelham as de contact; reusam a
// mesma UI de abas no DealPanel).
export const getDealActivities = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${id}/activities?${qs}`)
}

// Atividades de todos os deals de um funil numa única chamada (Agenda de
// Pipelines) — evita 1 request por card.
export const getPipelineActivities = (pipelineId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/pipelines/${pipelineId}/activities?${qs}`)
}

export const createDealActivity = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${id}/activities?${qs}`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export const getDealTimeline = (id, cursor, scope = 'deal') => {
  const qs = withClient(new URLSearchParams({ limit: '20', scope }))
  if (cursor) qs.set('cursor', cursor)
  return fetchApi(`/crm/deals/${id}/timeline?${qs}`)
}

export const getCrmContactTimeline = (id, cursor) => {
  const qs = withClient(new URLSearchParams({ limit: '20' }))
  if (cursor) qs.set('cursor', cursor)
  return fetchApi(`/crm/contacts/${id}/timeline?${qs}`)
}

// Mover o card (deal) no kanban: troca stage_id e grava deal_stage_transitions.
// Se o stage destino for de ganho/perdido, o backend fecha o negócio (status +
// data de fechamento). Ao cair numa etapa de perda, lossReasonId/justificativa
// registram o motivo.
export const moveDeal = (
  id,
  { toStageId, motivo = null, origemEvento = 'manual', lossReasonId = null, justificativa = null, ownerId }
) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${id}/move?${qs}`, {
    method: 'POST',
    body: JSON.stringify({
      to_stage_id: toStageId,
      ...(ownerId !== undefined ? { owner_id: ownerId } : {}),
      motivo,
      origem_evento: origemEvento,
      loss_reason_id: lossReasonId,
      justificativa,
    }),
  })
}

// Motivos de perda configuráveis por client (p/ marcar um deal como perdido).
export const getLossReasons = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/loss-reasons?${qs}`)
}

export const createLossReason = (nome) =>
  fetchApi('/crm/loss-reasons', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), nome }),
  })

// DealContacts — os Leads de um Deal (M:N).
export const getDealContacts = (dealId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/contacts?${qs}`)
}

export const linkDealContact = (dealId, { contactId, papel = null, isPrimary = false }) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/contacts?${qs}`, {
    method: 'POST',
    body: JSON.stringify({ contact_id: contactId, papel, is_primary: isPrimary }),
  })
}

export const updateDealContact = (dealId, contactId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/contacts/${contactId}?${qs}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export const unlinkDealContact = (dealId, contactId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/contacts/${contactId}?${qs}`, { method: 'DELETE' })
}

// Contact 360 — leituras agregadas p/ o painel lateral do lead (LeadPanel).
// Negócios do lead (via deal_contacts) com etapa/pipeline/probabilidade.
export const getCrmContactDeals = (contactId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contacts/${contactId}/deals?${qs}`)
}

// Atividades/tarefas vinculadas ao lead.
export const getCrmContactActivities = (contactId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contacts/${contactId}/activities?${qs}`)
}

// Cria uma atividade já vinculada ao lead (+ Nova atividade).
export const createCrmContactActivity = (contactId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contacts/${contactId}/activities?${qs}`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// Timeline sintetizada do lead (criação, etapas, negócios, atividades).
export const getCrmContactHistory = (contactId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contacts/${contactId}/history?${qs}`)
}

// Edita/conclui uma atividade (lead ou deal — id global). Ex.: { status: 'feito' },
// { prazo, prioridade, titulo, responsavel_id }. 'atrasado' é derivado na leitura.
export const updateActivity = (activityId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/activities/${activityId}?${qs}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

// Exclui uma atividade (lead ou deal — id global). Hard delete: activities
// não tem soft-delete.
export const deleteActivity = (activityId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/activities/${activityId}?${qs}`, { method: 'DELETE' })
}

// Notas do histórico, inclusive as antigas sem uma atividade correspondente.
export const updateCrmNote = (noteId, texto) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/notes/${noteId}?${qs}`, { method: 'PATCH', body: JSON.stringify({ texto }) })
}

export const deleteCrmNote = (noteId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/notes/${noteId}?${qs}`, { method: 'DELETE' })
}

// ---------------------------------------------------------------------------
// Catálogo — products (avulso) e services (recorrente)  [Fase 2 / migration 016]
// "Excluir" desativa (is_active=false); backend degrada a lista vazia se a 016
// ainda não foi aplicada. include_inactive=true traz também os desativados.
// ---------------------------------------------------------------------------
export const getProducts = (includeInactive = false) => {
  const params = withClient(new URLSearchParams(includeInactive ? { include_inactive: 'true' } : {}))
  return fetchApi(`/crm/products?${params}`)
}

export const createProduct = (data) =>
  fetchApi('/crm/products', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })

export const updateProduct = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/products/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const deleteProduct = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/products/${id}?${qs}`, { method: 'DELETE' })
}

export const getServices = (includeInactive = false) => {
  const params = withClient(new URLSearchParams(includeInactive ? { include_inactive: 'true' } : {}))
  return fetchApi(`/crm/services?${params}`)
}

export const createService = (data) =>
  fetchApi('/crm/services', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })

export const updateService = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/services/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const deleteService = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/services/${id}?${qs}`, { method: 'DELETE' })
}

// deal_items — o que foi vendido no deal (produto XOR serviço). valor_total vem
// calculado do banco (GENERATED). Passe productId OU serviceId (exatamente um).
export const getDealItems = (dealId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/items?${qs}`)
}

export const createDealItem = (
  dealId,
  { productId = null, serviceId = null, descricao = null, quantidade = 1, precoUnitario = 0, periodicidade = null }
) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/items?${qs}`, {
    method: 'POST',
    body: JSON.stringify({
      product_id: productId,
      service_id: serviceId,
      descricao,
      quantidade,
      preco_unitario: precoUnitario,
      periodicidade,
    }),
  })
}

export const updateDealItem = (dealId, itemId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/items/${itemId}?${qs}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export const deleteDealItem = (dealId, itemId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/items/${itemId}?${qs}`, { method: 'DELETE' })
}

// ---------------------------------------------------------------------------
// Contratos & Projetos — deal ganho recorrente → contrato → projeto  [F3 / 016]
// Contratos ficam em /crm/contracts (filtro dealId), NÃO em /deals/{id}/contacts
// (que são os Leads do deal). Backend degrada a lista vazia se a 016 não foi
// aplicada. Excluir um contrato apaga em cascata seus projetos.
// ---------------------------------------------------------------------------
export const getContracts = (dealId = null) => {
  const params = withClient(new URLSearchParams(dealId ? { deal_id: dealId } : {}))
  return fetchApi(`/crm/contracts?${params}`)
}

export const createContract = (data) =>
  fetchApi('/crm/contracts', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })

export const deleteContract = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/contracts/${id}?${qs}`, { method: 'DELETE' })
}

// Projetos (entregas) de um contrato — filtro contractId.
export const getProjects = (contractId = null) => {
  const params = withClient(new URLSearchParams(contractId ? { contract_id: contractId } : {}))
  return fetchApi(`/crm/projects?${params}`)
}

export const createProject = (data) =>
  fetchApi('/crm/projects', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })

export const updateProject = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/projects/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const deleteProject = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/projects/${id}?${qs}`, { method: 'DELETE' })
}

// ---------------------------------------------------------------------------
// Módulo PROJETOS — gerenciador de projetos Kanban (operação).
// Endpoints sob /api/projetos. Independente das funções legadas de /crm/projects
// (acima, presas a contrato). GET injeta client_id na query (withClient); POST
// manda client_id no corpo. Guardado por projetos_available no backend.
// ---------------------------------------------------------------------------
// Dashboard do módulo Operação ("Operação de projetos").
// `from`/`to` (yyyy-mm-dd) definem o período dos KPIs, séries e listas; sem eles
// o backend usa os últimos `meses`. `cliente` (empresa/contato do projeto),
// `responsavel` (Equipe) e `categoria` (template_key; '' = Sem categoria) são os
// três seletores da barra de filtros da tela.
// Blocos: kpis, evolucao, status_projetos, atividades, equipes,
// projetos_em_risco, horas_por_cliente, insight e filtros. Horas e capacidade
// vêm com `estimado` (derivadas das atividades); a rosca de status e o board de
// atividades vêm com `snapshot`.
export const getOperacaoDashboard = ({
  from, to, meses = 12, cliente, responsavel, categoria,
} = {}) => {
  const qs = withClient(new URLSearchParams({ meses: String(meses) }))
  if (from) qs.set('from', from)
  if (to) qs.set('to', to)
  if (cliente) qs.set('cliente', cliente)
  if (responsavel) qs.set('responsavel', responsavel)
  // categoria aceita '' como valor legítimo ("Sem categoria"): só omite null.
  if (categoria != null) qs.set('categoria', categoria)
  return fetchApi(`/projetos/dashboard?${qs}`)
}

// Resumo e detalhes usam o mesmo recorte e fixam o tenant da requisição.
const operacaoQuery = ({ clientId, from, to, cliente, responsavel, categoria, ...rest } = {}) => {
  const qs = withClient(new URLSearchParams(rest))
  if (clientId) qs.set('client_id', clientId)
  if (from) qs.set('from', from)
  if (to) qs.set('to', to)
  if (cliente) qs.set('cliente', cliente)
  if (responsavel) qs.set('responsavel', responsavel)
  if (categoria != null && categoria !== '__todas__') qs.set('categoria', categoria)
  return qs
}
export const getOperacaoOverview = (filters) => fetchApi(`/projetos/overview?${operacaoQuery(filters)}`)
export const getOperacaoItems = (filters) => fetchApi(`/projetos/overview/items?${operacaoQuery(filters)}`)
export const getOperacaoTasks = (filters) => fetchApi(`/projetos/overview/tasks?${operacaoQuery(filters)}`)
export const getOperacaoTeam = (filters) => fetchApi(`/projetos/overview/team?${operacaoQuery(filters)}`)
export const getOperacaoPerson = (id, filters) => fetchApi(`/projetos/overview/team/${encodeURIComponent(id)}?${operacaoQuery(filters)}`)
export const getOperacaoCommercialPipelines = (filters) => fetchApi(`/projetos/overview/commercial-pipelines?${operacaoQuery(filters)}`)
export const getOperacaoCommercialBoard = (id, filters) => fetchApi(`/projetos/overview/commercial-pipelines/${id}/board?${operacaoQuery(filters)}`)
export const getOperacaoCommercialTask = (id) => fetchApi(`/projetos/overview/commercial-tasks/${id}?${operacaoQuery()}`)

export const pjListTemplates = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/templates?${qs}`)
}

// importar/criar template custom (cola de JSON ou criação manual)
export const pjImportTemplate = (data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/templates?${qs}`, { method: 'POST', body: JSON.stringify(data) })
}

export const pjDeleteTemplate = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/templates/${id}?${qs}`, { method: 'DELETE' })
}

export const pjExportTemplate = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/templates/${id}/export?${qs}`)
}

// salvar o quadro atual de um projeto como template custom
export const pjSaveProjectAsTemplate = (projectId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/${projectId}/save-as-template?${qs}`, {
    method: 'POST', body: JSON.stringify(data),
  })
}

export const pjListProjects = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos?${qs}`)
}

export const pjGetProject = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/${id}?${qs}`)
}

export const pjCreateProject = (data) =>
  fetchApi('/projetos', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })

export const pjUpdateProject = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const pjDeleteProject = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/${id}?${qs}`, { method: 'DELETE' })
}

export const pjGetBoard = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/${id}/board?${qs}`)
}

// Tarefas
export const pjCreateTask = (projectId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/${projectId}/tasks?${qs}`, { method: 'POST', body: JSON.stringify(data) })
}

export const pjGetTask = (taskId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}?${qs}`)
}

export const pjUpdateTask = (taskId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const pjDeleteTask = (taskId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}?${qs}`, { method: 'DELETE' })
}

// Colunas (stages) do Kanban do projeto — projetos.stages (separadas das etapas do CRM).
export const pjCreateStage = (projectId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/${projectId}/stages?${qs}`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export const pjUpdateStage = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/stages/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const pjDeleteStage = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/stages/${id}?${qs}`, { method: 'DELETE' })
}

export const pjTransferTask = (taskId, projectId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/transfer?${qs}`, {
    method: 'POST', body: JSON.stringify({ project_id: projectId }),
  })
}

export const pjMoveTask = (taskId, { toStageId, ordem = null }) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/move?${qs}`, {
    method: 'POST',
    body: JSON.stringify({ to_stage_id: toStageId, ordem }),
  })
}

export const pjGetTaskHistory = (taskId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/history?${qs}`)
}

// Responsáveis (N:N)
export const pjAddAssignee = (taskId, userId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/assignees?${qs}`, {
    method: 'POST', body: JSON.stringify({ user_id: userId }),
  })
}

export const pjRemoveAssignee = (taskId, userId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/assignees/${userId}?${qs}`, { method: 'DELETE' })
}

// Checklist
export const pjListChecklist = (taskId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/checklist?${qs}`)
}

export const pjCreateChecklistItem = (taskId, texto) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/checklist?${qs}`, {
    method: 'POST', body: JSON.stringify({ texto }),
  })
}

export const pjUpdateChecklistItem = (itemId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/checklist/${itemId}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const pjDeleteChecklistItem = (itemId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/checklist/${itemId}?${qs}`, { method: 'DELETE' })
}

// Comentários
export const crmListActivityComments = (activityId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/activities/${activityId}/comments?${qs}`)
}

export const crmCreateActivityComment = (activityId, corpo) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/activities/${activityId}/comments?${qs}`, {
    method: 'POST', body: JSON.stringify({ corpo }),
  })
}

export const pjListComments = (taskId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/comments?${qs}`)
}

export const pjCreateComment = (taskId, corpo) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/comments?${qs}`, {
    method: 'POST', body: JSON.stringify({ corpo }),
  })
}

// Anexos — binário no Supabase Storage (bucket privado, mediado pelo backend).
export const pjListAttachments = (taskId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/attachments?${qs}`)
}

// 1) pede URL de upload assinada → { upload_url, storage_path }
export const pjSignUpload = (taskId, nome) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/attachments/sign-upload?${qs}`, {
    method: 'POST', body: JSON.stringify({ nome }),
  })
}

// 3) registra o metadado após o PUT do arquivo
export const pjCreateAttachment = (taskId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/tasks/${taskId}/attachments?${qs}`, {
    method: 'POST', body: JSON.stringify(data),
  })
}

// download: devolve { url, nome } com URL assinada temporária
export const pjDownloadAttachment = (attachmentId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/attachments/${attachmentId}/download?${qs}`)
}

export const pjDeleteAttachment = (attachmentId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/projetos/attachments/${attachmentId}?${qs}`, { method: 'DELETE' })
}

// Upload direto do arquivo na URL assinada (PUT, fora do fetchApi — sem JWT do app).
export async function pjUploadToSignedUrl(uploadUrl, file) {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  })
  if (!res.ok) throw new Error(`Falha no upload (${res.status})`)
}

// Arquivos do negócio (aba "Arquivos" do painel do deal) — mesmo fluxo dos anexos
// de tarefa: sign-upload → PUT na URL assinada → registra o metadado.
export const getDealAttachments = (dealId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/attachments?${qs}`)
}

export const signDealAttachmentUpload = (dealId, nome) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/attachments/sign-upload?${qs}`, {
    method: 'POST', body: JSON.stringify({ nome }),
  })
}

export const createDealAttachment = (dealId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deals/${dealId}/attachments?${qs}`, {
    method: 'POST', body: JSON.stringify(data),
  })
}

// devolve { url, nome } com URL assinada temporária
export const downloadDealAttachment = (attachmentId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deal-attachments/${attachmentId}/download?${qs}`)
}

export const deleteDealAttachment = (attachmentId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/crm/deal-attachments/${attachmentId}?${qs}`, { method: 'DELETE' })
}

// ---------------------------------------------------------------------------
// Financeiro (F2 / migration 017) — contas a pagar/receber, saldo, fluxo de caixa.
// Endpoints sob /api/financeiro. Leituras e mutações por id injetam client_id na
// query (withClient); criações mandam client_id no corpo (getSelectedClientId),
// pois o backend lê client_id do body no POST. Guardado por financeiro_available
// no backend: sem a 017, GET degrada a vazio/zeros e POST/PATCH/DELETE dão 503.
// ---------------------------------------------------------------------------
// Dashboard do financeiro. `from`/`to` (yyyy-mm-dd) definem o período dos KPIs;
// `meses` é o tamanho das séries dos gráficos, que terminam no mês de `to`.
export const getFinanceiroDashboard = ({ from, to, meses = 12 } = {}) => {
  const qs = withClient(new URLSearchParams({ meses: String(meses) }))
  if (from) qs.set('from', from)
  if (to) qs.set('to', to)
  return fetchApi(`/financeiro/dashboard?${qs}`)
}

// Contas a pagar
export const getContasPagar = (filtros = {}) => {
  const qs = withClient(new URLSearchParams(
    Object.fromEntries(Object.entries(filtros).filter(([, v]) => v != null && v !== ''))))
  return fetchApi(`/financeiro/contas-pagar?${qs}`)
}
export const createContaPagar = (data) =>
  fetchApi('/financeiro/contas-pagar', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })
export const updateContaPagar = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/financeiro/contas-pagar/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}
export const deleteContaPagar = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/financeiro/contas-pagar/${id}?${qs}`, { method: 'DELETE' })
}

// Contas a receber
export const getContasReceber = (filtros = {}) => {
  const qs = withClient(new URLSearchParams(
    Object.fromEntries(Object.entries(filtros).filter(([, v]) => v != null && v !== ''))))
  return fetchApi(`/financeiro/contas-receber?${qs}`)
}
export const createContaReceber = (data) =>
  fetchApi('/financeiro/contas-receber', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })
export const updateContaReceber = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/financeiro/contas-receber/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}
export const deleteContaReceber = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/financeiro/contas-receber/${id}?${qs}`, { method: 'DELETE' })
}

// Comprovantes (recibo/nota) — binário no bucket privado `financeiro-comprovantes`.
// Fluxo: sign-upload → PUT do arquivo na URL assinada → grava comprovante_path
// no lançamento. Sem Storage configurado o backend devolve 503.
const signUploadComprovante = (nome, tipo = 'pagar') => {
  const qs = withClient(new URLSearchParams({ tipo }))
  return fetchApi(`/financeiro/comprovantes/sign-upload?${qs}`, {
    method: 'POST',
    body: JSON.stringify({ nome }),
  })
}
export const getComprovanteUrl = (path) => {
  const qs = withClient(new URLSearchParams({ path }))
  return fetchApi(`/financeiro/comprovantes/download?${qs}`)
}

// Envia o arquivo e devolve o storage_path para gravar no lançamento.
export async function uploadComprovante(file, tipo = 'pagar') {
  const { upload_url, storage_path } = await signUploadComprovante(file.name, tipo)
  await pjUploadToSignedUrl(upload_url, file)
  return storage_path
}

// Saldo (contas financeiras)
export const getSaldos = (includeInactive = false) => {
  const qs = withClient(new URLSearchParams(includeInactive ? { include_inactive: 'true' } : {}))
  return fetchApi(`/financeiro/saldos?${qs}`)
}
export const createSaldo = (data) =>
  fetchApi('/financeiro/saldos', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })
export const updateSaldo = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/financeiro/saldos/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}
export const deleteSaldo = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/financeiro/saldos/${id}?${qs}`, { method: 'DELETE' })
}

export const fmtBRL = (v) =>
  `R$ ${(+v).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
export const fmtNum = (v) => (+v).toLocaleString('pt-BR')
export const fmtPct = (v) => `${(+v).toFixed(2)}%`
export const fmtMult = (v) => `${(+v).toFixed(1)}x`

// ---------------------------------------------------------------------------
// Módulo ATENDIMENTO (migration 024) — central de conversas em tempo real.
// Endpoints sob /api/atendimento. GET injeta client_id na query (withClient);
// POST de criar conversa manda client_id no corpo. Guardado por
// atendimento_available no backend.
// ---------------------------------------------------------------------------
export const atListConversations = ({ filtro = 'todas', busca = '', contactId = null } = {}) => {
  const qs = withClient(new URLSearchParams())
  if (filtro) qs.set('filtro', filtro)
  if (busca) qs.set('busca', busca)
  if (contactId) qs.set('contact_id', contactId)
  return fetchApi(`/atendimento/conversations?${qs}`)
}

export const atGetConversation = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/conversations/${id}?${qs}`)
}

export const atCreateConversation = (data) =>
  fetchApi('/atendimento/conversations', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })

export const atUpdateConversation = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/conversations/${id}?${qs}`, {
    method: 'PATCH', body: JSON.stringify(data),
  })
}

export const atDeleteConversation = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/conversations/${id}?${qs}`, { method: 'DELETE' })
}

// assumir (userId = próprio), transferir (outro) ou desatribuir (null)
export const atAssignConversation = (id, userId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/conversations/${id}/assign?${qs}`, {
    method: 'POST', body: JSON.stringify({ user_id: userId }),
  })
}

export const atMarkRead = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/conversations/${id}/read?${qs}`, { method: 'POST' })
}

export const atMarkUnread = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/conversations/${id}/unread?${qs}`, { method: 'POST' })
}

// Presença do operador (disponivel | ausente | offline) — persistida por cliente
// (migration 050). As automações usam para rotear (condição e rodízio/carga).
export const atListPresence = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/presence?${qs}`)
}

export const atSetPresence = (status) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/presence?${qs}`, {
    method: 'PUT', body: JSON.stringify({ status }),
  })
}

// Mensagens
export const atListMessages = (convId, { before = null, limit = 50 } = {}) => {
  const qs = withClient(new URLSearchParams())
  if (before) qs.set('before', before)
  if (limit) qs.set('limit', String(limit))
  return fetchApi(`/atendimento/conversations/${convId}/messages?${qs}`)
}

export const atSendMessage = (convId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/conversations/${convId}/messages?${qs}`, {
    method: 'POST', body: JSON.stringify(data),
  })
}

// Anexos (metadado; binário no Storage via signed URL)
export const atSignUpload = (msgId, nome) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/messages/${msgId}/attachments/sign-upload?${qs}`, {
    method: 'POST', body: JSON.stringify({ nome }),
  })
}

export const atCreateAttachment = (msgId, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/messages/${msgId}/attachments?${qs}`, {
    method: 'POST', body: JSON.stringify(data),
  })
}

export const atDownloadAttachment = (attId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/atendimento/attachments/${attId}/download?${qs}`)
}

export async function atUploadToSignedUrl(uploadUrl, file) {
  return pjUploadToSignedUrl(uploadUrl, file)
}

// ---------------------------------------------------------------------------
// Submódulo AGENDA do Comercial (migration 034) — endpoints sob /api/agenda.
// O evento é uma `activities` com inicio_at (não há tabela de eventos), então o
// agendamento criado aqui também aparece nas atividades do lead/deal.
// GET injeta client_id na query (withClient); POST manda client_id no corpo.
// ---------------------------------------------------------------------------

// Eventos que intersectam a faixa. `from`/`to` em ISO 8601 (semana visível).
export const agListEvents = ({ from, to, ownerId = null }) => {
  const qs = withClient(new URLSearchParams({ from, to }))
  if (ownerId) qs.append('owner_id', ownerId)
  return fetchApi(`/agenda/events?${qs}`)
}

export const agListUpcoming = ({ ownerId = null, limit = 5 } = {}) => {
  const qs = withClient(new URLSearchParams({ limit: String(limit) }))
  if (ownerId) qs.append('owner_id', ownerId)
  return fetchApi(`/agenda/events/upcoming?${qs}`)
}

export const agCreateEvent = (data) =>
  fetchApi('/agenda/events', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId(), ...data }),
  })

export const agUpdateEvent = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/agenda/events/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const agDeleteEvent = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/agenda/events/${id}?${qs}`, { method: 'DELETE' })
}

// Disponibilidade (card "Minha disponibilidade"). Sem userId = usuário logado.
export const agGetAvailability = (userId = null) => {
  const qs = withClient(new URLSearchParams())
  if (userId) qs.append('user_id', userId)
  return fetchApi(`/agenda/availability?${qs}`)
}

export const agSaveAvailability = (data, userId = null) => {
  const qs = withClient(new URLSearchParams())
  if (userId) qs.append('user_id', userId)
  return fetchApi(`/agenda/availability?${qs}`, { method: 'PUT', body: JSON.stringify(data) })
}

// Busca de lead/negócio para o campo "vincular" do modal de agendamento.
export const agSearchVinculos = (q) => {
  const qs = withClient(new URLSearchParams({ q }))
  return fetchApi(`/agenda/vinculos?${qs}`)
}

// --- Google Calendar (issue #72) -------------------------------------------
// O consent reusa o redirect_uri já registrado do Google Ads; quem diz à página
// de callback qual integração finalizar é o `provider` assinado dentro do state.
export const agCalendarStatus = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/integrations/google-calendar/status?${qs}`)
}

export const agCalendarAuthorize = () =>
  fetchApi('/integrations/google-calendar/authorize', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId() }),
  })

export const agCalendarCallback = ({ code, state, clientId, calendarId = null }) =>
  fetchApi('/integrations/google-calendar/callback', {
    method: 'POST',
    body: JSON.stringify({ code, state, client_id: clientId, calendar_id: calendarId }),
  })

export const agCalendarDisconnect = () =>
  fetchApi('/integrations/google-calendar/disconnect', {
    method: 'POST',
    body: JSON.stringify({ client_id: getSelectedClientId() }),
  })

export const agCalendarSync = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/agenda/calendar/sync?${qs}`, { method: 'POST' })
}

// ---------------------------------------------------------------------------
// Módulo AUTOMAÇÕES (issue #137, migration 046) — endpoints sob /api/automations.
// O fluxo (`definition`) é o grafo do @xyflow/react: { nodes, edges }.
// Toda automação lida/salva volta com `errors` (o que impede ativar).
// ---------------------------------------------------------------------------

// Tipos de nó (formulário de cada um) + galeria de modelos prontos.
export const autCatalog = () => fetchApi('/automations/catalog')

// Formulários de Lead Ads das Páginas do cliente (seletor do gatilho "Lead Ads: formulário recebido", #301).
export const autLeadForms = (clientId) =>
  fetchApi(`/automations/lead-forms?client_id=${encodeURIComponent(clientId)}`)

// Leads de formulário guardados que a automação ainda não processou (inclusive os de antes de
// ela ser ativada) e o reprocesso deles (#306).
export const autPendingLeads = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/automations/${id}/pending-leads?${qs}`)
}

export const autReprocessLeads = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/automations/${id}/reprocess-leads?${qs}`, { method: 'POST' })
}

export const autList = () => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/automations?${qs}`)
}

// `templateKey` cria a partir de um modelo da galeria; sem ele nasce vazia.
export const autCreate = ({ nome, descricao = null, templateKey = null, definition = null }) =>
  fetchApi('/automations', {
    method: 'POST',
    body: JSON.stringify({
      client_id: getSelectedClientId(), nome, descricao,
      template_key: templateKey, definition,
    }),
  })

export const autGet = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/automations/${id}?${qs}`)
}

// PATCH parcial: { nome?, descricao?, definition?, ativo? }. Ativar um fluxo
// inválido volta 422 com detail = { message, errors }.
export const autUpdate = (id, data) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/automations/${id}?${qs}`, { method: 'PATCH', body: JSON.stringify(data) })
}

export const autDelete = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/automations/${id}?${qs}`, { method: 'DELETE' })
}

export const autRuns = (id, { limit = 50 } = {}) => {
  const qs = withClient(new URLSearchParams({ limit: String(limit) }))
  return fetchApi(`/automations/${id}/runs?${qs}`)
}

// Detalhe de uma execução com o log por nó (`steps`).
export const autRun = (runId) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/automations/runs/${runId}?${qs}`)
}

// URL pública do gatilho "Webhook" (issue #139). API_URL pode ser relativa
// (/api) em produção — resolve contra a origem da página para mostrar a URL
// completa que o sistema externo precisa chamar.
export const autWebhookUrl = (token) =>
  (token ? new URL(`${API_URL}/public/automations/hooks/${token}`, window.location.origin).href : null)

// URL de teste do gatilho "Webhook": só responde depois de autTestListen (2 min, uma chamada).
export const autWebhookTestUrl = (token) =>
  (token ? new URL(`${API_URL}/public/automations/hooks-test/${token}`, window.location.origin).href : null)

// Modo de teste: roda o fluxo do canvas (salvo ou não) uma vez, com as ações reais.
// `data`: contexto do gatilho (ex.: { contact_id, deal_id }); no Webhook, vazio = última chamada.
export const autTest = (id, { definition = null, data = null } = {}) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/automations/${id}/test?${qs}`, { method: 'POST', body: JSON.stringify({ definition, data }) })
}

export const autTestListen = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/automations/${id}/test/listen?${qs}`, { method: 'POST' })
}

export const autTestListenStop = (id) => {
  const qs = withClient(new URLSearchParams())
  return fetchApi(`/automations/${id}/test/listen?${qs}`, { method: 'DELETE' })
}
